import { useCallback, useEffect, useRef, useState } from 'react';
import type { JourneyAddedCard } from '~/components/JourneyPayment/types';
import { usePaymentGatewayPublicKey } from '~/hooks/usePaymentGatewayPublicKey';
import { changeContractCard, getCardChangeContract, type CardChangeContract } from '~/services/modules/cardChange';
import { tokenizeCardWithPagarme, type CardInput, type CardToken } from '~/services/modules/paymentGateway';
import { reportError } from '~/utils/reportError';
import { getCardChangeFailureMessage, isCardChangeContractMissing, resolveCardChangeOrganization } from './cardChange';

export type CardChangeStep = 'summary' | 'form' | 'processing' | 'outcome';
export type CardChangeOutcome = 'success' | 'failure';
/** Só o que a tela de sucesso mostra: número, validade e CVV não ficam no estado depois da troca. */
export type CardChangeChangedCard = Pick<JourneyAddedCard, 'brand' | 'lastFour'>;
/** `missing`: link sem contrato (404); `error`: falha temporária, com «Tentar novamente». */
export type CardChangeLoadState = 'loading' | 'ready' | 'missing' | 'error';

type Options = {
  contractHash: string;
  organizationId: string | null;
  /** Injetável para stories/testes; o padrão tokeniza no Pagar.me. */
  tokenize?: (publicKey: string | null | undefined, card: CardInput) => Promise<CardToken>;
};

/**
 * Estado da troca de cartão 2026: carrega o contrato, recebe o cartão novo do formulário e troca o cartão da
 * assinatura. O token do Pagar.me dura 60 s e é de uso único, então cada tentativa (inclusive «Tentar
 * novamente») tokeniza de novo a partir dos dados guardados só em memória.
 */
export function useCardChangeJourney({ contractHash, organizationId, tokenize = tokenizeCardWithPagarme }: Options) {
  const orgId = resolveCardChangeOrganization(organizationId);
  const { publicKey, loading: publicKeyLoading } = usePaymentGatewayPublicKey(orgId);

  const [loadState, setLoadState] = useState<CardChangeLoadState>('loading');
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [contract, setContract] = useState<CardChangeContract | null>(null);
  const [step, setStep] = useState<CardChangeStep>('summary');
  const [outcome, setOutcome] = useState<CardChangeOutcome | null>(null);
  const [failureMessage, setFailureMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [changedCard, setChangedCard] = useState<CardChangeChangedCard | null>(null);
  const pendingCard = useRef<JourneyAddedCard | null>(null);

  useEffect(() => {
    let active = true;
    setLoadState('loading');
    getCardChangeContract(contractHash, orgId)
      .then((loaded) => {
        if (!active) return;
        setContract(loaded);
        setLoadState('ready');
      })
      .catch((error: unknown) => {
        if (!active) return;
        reportError('Erro ao carregar o contrato da troca de cartão 2026', error, {
          context: 'card_change_load',
          flow: 'card_change',
          contractHash,
          attempt: loadAttempt + 1
        });
        setLoadState(isCardChangeContractMissing(error) ? 'missing' : 'error');
      });
    return () => {
      active = false;
    };
  }, [contractHash, loadAttempt, orgId]);

  /** «Tentar novamente» da falha temporária no carregamento: refaz o `GET Contracts/{hash}`. */
  const reloadContract = useCallback(() => setLoadAttempt((current) => current + 1), []);

  const change = useCallback(
    async (card: JourneyAddedCard) => {
      if (!contract) return;
      pendingCard.current = card;
      setSubmitting(true);
      setStep('processing');
      let stage: 'tokenize' | 'change' = 'tokenize';
      try {
        const { token } = await tokenize(publicKey, card.cardInput);
        stage = 'change';
        await changeContractCard(contractHash, contract.payment_method, token, orgId);
        // Dados do cartão saem da memória assim que a troca é concluída.
        pendingCard.current = null;
        setChangedCard({ brand: card.brand, lastFour: card.lastFour });
        setFailureMessage(null);
        setOutcome('success');
      } catch (error: unknown) {
        setFailureMessage(getCardChangeFailureMessage(error));
        setOutcome('failure');
        reportError('Erro na troca de cartão 2026', error, {
          context: stage === 'tokenize' ? 'card_change_tokenize' : 'card_change_patch',
          flow: 'card_change',
          contractHash,
          paymentMethod: contract.payment_method
        });
      } finally {
        setSubmitting(false);
        setStep('outcome');
      }
    },
    [contract, contractHash, orgId, publicKey, tokenize]
  );

  const retry = useCallback(() => {
    if (pendingCard.current) void change(pendingCard.current);
  }, [change]);

  /** «Alterar dados do cartão de crédito» no resumo do contrato. */
  const startCardChange = useCallback(() => setStep('form'), []);

  const useAnotherCard = useCallback(() => {
    pendingCard.current = null;
    setOutcome(null);
    setFailureMessage(null);
    setStep('form');
  }, []);

  return {
    loadState,
    contract,
    step,
    outcome,
    failureMessage,
    submitting,
    changedCard,
    publicKey,
    publicKeyLoading,
    actions: { startCardChange, submitCard: change, retry, useAnotherCard, reloadContract }
  };
}
